/**
 * om-facets.js
 * Custom faceted filtering for the collection grid.
 *
 * Every filter value already carries the exact URL Shopify wants
 * (url_to_add / url_to_remove), so this never has to build query strings by
 * hand - it takes the URL off the clicked control, fetches that page's section
 * HTML, and swaps in the new filters and grid. Price range is the one
 * exception, since the shopper types arbitrary numbers.
 *
 * Uses the Section Rendering API, so only this section is re-rendered rather
 * than the whole page, and pushState keeps the URL shareable and the browser
 * Back button working.
 */
(function () {
  'use strict';

  if (window.__omFacets) return;
  window.__omFacets = true;

  var SECTION_SEL = '.om-collection-section';
  var GRID_SEL = '#ProductGridContainer';
  var FACETS_SEL = '[data-om-facets]';

  function section() { return document.querySelector(SECTION_SEL); }
  function sectionId() {
    var el = section();
    return el ? el.getAttribute('data-section-id') : null;
  }

  var inflight = null;

  function setLoading(on) {
    var el = section();
    if (el) el.classList.toggle('om-is-loading', on);
  }

  /**
   * Fetch `url`, render just our section, and swap the filters + grid in.
   */
  function go(url, push) {
    var id = sectionId();
    if (!id) { window.location = url; return; }

    if (inflight) inflight.abort();
    var controller = new AbortController();
    inflight = controller;

    setLoading(true);

    var target = new URL(url, window.location.origin);
    target.searchParams.set('section_id', id);

    fetch(target.toString(), { signal: controller.signal })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');

        var freshGrid = doc.querySelector(GRID_SEL);
        var liveGrid = document.querySelector(GRID_SEL);
        if (freshGrid && liveGrid) liveGrid.innerHTML = freshGrid.innerHTML;

        var freshFacets = doc.querySelector(FACETS_SEL);
        var liveFacets = document.querySelector(FACETS_SEL);
        var wasOpen = liveFacets && liveFacets.classList.contains('is-open');
        if (freshFacets && liveFacets) {
          // Keep which groups the shopper had expanded.
          var openGroups = {};
          liveFacets.querySelectorAll('[data-om-facet-group]').forEach(function (d) {
            openGroups[d.getAttribute('data-om-facet-group')] = d.open;
          });

          liveFacets.innerHTML = freshFacets.innerHTML;

          liveFacets.querySelectorAll('[data-om-facet-group]').forEach(function (d) {
            var k = d.getAttribute('data-om-facet-group');
            if (k in openGroups) d.open = openGroups[k];
          });

          if (wasOpen) liveFacets.classList.add('is-open');
        }

        if (push) {
          var clean = new URL(url, window.location.origin);
          clean.searchParams.delete('section_id');
          window.history.pushState({ omFacets: true }, '', clean.toString());
        }

        document.dispatchEvent(new CustomEvent('om:facets:updated'));

        var anchor = document.querySelector(SECTION_SEL);
        if (anchor && window.innerWidth >= 750) {
          var top = anchor.getBoundingClientRect().top + window.scrollY - 100;
          if (window.scrollY > top) window.scrollTo({ top: top, behavior: 'smooth' });
        }
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        window.location = url;   // never leave the shopper stuck
      })
      .then(function () {
        inflight = null;
        setLoading(false);
      });
  }

  // ---------------------------------------------------------- checkboxes ---

  document.addEventListener('change', function (e) {
    var input = e.target.closest('[data-om-facet-input]');
    if (!input) return;
    var url = input.getAttribute('data-url');
    if (url) go(url, true);
  });

  // --------------------------------------------------------------- sort ---

  document.addEventListener('change', function (e) {
    var sel = e.target.closest('[data-om-sort]');
    if (!sel) return;
    var url = new URL(window.location.href);
    url.searchParams.set('sort_by', sel.value);
    url.searchParams.delete('page');
    go(url.toString(), true);
  });

  // ------------------------------------------- chips, clear all, paging ---

  document.addEventListener('click', function (e) {
    var link = e.target.closest('[data-om-facet-link]');
    if (link) {
      e.preventDefault();
      go(link.getAttribute('href'), true);
      return;
    }

    var page = e.target.closest('.pagination__item, [data-om-page]');
    if (page && page.tagName === 'A' && page.getAttribute('href')) {
      e.preventDefault();
      go(page.getAttribute('href'), true);
    }
  });

  // -------------------------------------------------------- price range ---

  function applyRange(box) {
    var ceiling = parseFloat(box.getAttribute('data-ceiling')) || 0;
    var minEl = box.querySelector('[data-om-range-min]');
    var maxEl = box.querySelector('[data-om-range-max]');

    var min = parseFloat(minEl.value);
    var max = parseFloat(maxEl.value);
    if (isNaN(min) || min < 0) min = 0;
    if (isNaN(max) || max > ceiling) max = ceiling;
    if (min > max) { var t = min; min = max; max = t; }

    minEl.value = min;
    maxEl.value = max;

    var url = new URL(window.location.href);
    url.searchParams.delete('page');

    if (min > 0) url.searchParams.set(box.getAttribute('data-min-name'), min);
    else url.searchParams.delete(box.getAttribute('data-min-name'));

    if (max < ceiling) url.searchParams.set(box.getAttribute('data-max-name'), max);
    else url.searchParams.delete(box.getAttribute('data-max-name'));

    go(url.toString(), true);
  }

  function paintRange(box) {
    var ceiling = parseFloat(box.getAttribute('data-ceiling')) || 1;
    var minEl = box.querySelector('[data-om-range-min]');
    var maxEl = box.querySelector('[data-om-range-max]');
    var fill = box.querySelector('[data-om-range-fill]');
    if (!fill) return;
    var min = Math.max(0, parseFloat(minEl.value) || 0);
    var max = Math.min(ceiling, parseFloat(maxEl.value) || ceiling);
    fill.style.left = (min / ceiling) * 100 + '%';
    fill.style.right = (100 - (max / ceiling) * 100) + '%';
  }

  document.addEventListener('click', function (e) {
    var apply = e.target.closest('[data-om-range-apply]');
    if (!apply) return;
    var box = apply.closest('[data-om-range]');
    if (box) applyRange(box);
  });

  document.addEventListener('input', function (e) {
    var input = e.target.closest('[data-om-range-min], [data-om-range-max]');
    if (!input) return;
    var box = input.closest('[data-om-range]');
    if (box) paintRange(box);
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter') return;
    var input = e.target.closest('[data-om-range-min], [data-om-range-max]');
    if (!input) return;
    e.preventDefault();
    var box = input.closest('[data-om-range]');
    if (box) applyRange(box);
  });

  // ---------------------------------------------------- in-group search ---

  document.addEventListener('input', function (e) {
    var search = e.target.closest('[data-om-facet-search]');
    if (!search) return;

    var body = search.closest('.om-facet__body');
    var q = search.value.trim().toLowerCase();
    var shown = 0;

    body.querySelectorAll('[data-om-facet-value]').forEach(function (li) {
      var hit = !q || li.getAttribute('data-om-facet-value').indexOf(q) !== -1;
      li.hidden = !hit;
      if (hit) shown++;
    });

    var none = body.querySelector('[data-om-facet-none]');
    if (none) none.hidden = shown !== 0;
  });

  // ------------------------------------------------------ mobile drawer ---

  function openDrawer(on) {
    var wrap = document.querySelector(FACETS_SEL);
    if (!wrap) return;
    wrap.classList.toggle('is-open', on);
    var scrim = wrap.querySelector('.om-facets__scrim');
    if (scrim) scrim.hidden = !on;
    document.body.style.overflow = on && window.innerWidth < 990 ? 'hidden' : '';
    var btn = wrap.querySelector('[data-om-facets-open]');
    if (btn) btn.setAttribute('aria-expanded', String(on));
  }

  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-om-facets-open]')) { openDrawer(true); return; }
    if (e.target.closest('[data-om-facets-close]')) { openDrawer(false); }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') openDrawer(false);
  });

  // ------------------------------------------------------------ history ---

  window.addEventListener('popstate', function () {
    go(window.location.href, false);
  });

  // paint any price ranges present on first load and after each update
  function paintAll() {
    document.querySelectorAll('[data-om-range]').forEach(paintRange);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', paintAll);
  } else {
    paintAll();
  }
  document.addEventListener('om:facets:updated', paintAll);
})();
